import { Box, Button, ButtonGroup, Flex } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { getFromUrl } from '../../api/Api';
import useRangeChange from '../../hooks/useRangeChange';
import { colors } from '../../theme/colors';
import {
  buildAnalyticsQuery,
  isAnalyticsQueryReady
} from '../../utils/dateRange';
import Card from '../Dashboard/Card';
import CardHeader from '../Dashboard/CardHeader';

const AGE_BRACKET_ORDER = [
  '6-15 years',
  '16-17 years',
  '18-25 years',
  '26-35 years',
  '36-45 years',
  '46 years and above'
] as const;

const GENDER_COLORS: Record<string, string> = {
  ALL: colors.dark_green,
  MALE: '#F2AC57',
  FEMALE: colors.accent,
  OTHER: '#9F7AEA'
};

const AgeRatioChart = () => {
  const [chartData, setChartData] = useState<
    { ageRange: string; value: number }[]
  >([]);
  const {
    range,
    startDate,
    endDate,
    handleChange,
    handleStartDateChange,
    handleEndDateChange
  } = useRangeChange();
  const [gender, setGender] = useState<string>();
  const [selectedGender, setSelectedGender] = useState('ALL');

  useEffect(() => {
    const getAgeRatio = async () => {
      if (!isAnalyticsQueryReady(range, startDate, endDate)) {
        setChartData([]);
        return;
      }

      try {
        const query = buildAnalyticsQuery(range, startDate, endDate);
        const genderQuery = gender ? `&&gender=${gender}` : '';
        const result = await getFromUrl(
          `users/age/ratio?${query}${genderQuery}`
        );
        const transformed = AGE_BRACKET_ORDER.map((bracket) => {
          const ratio = (result[bracket] as number) || 0;
          // Keep one decimal so small brackets (e.g. 26-35 at ~0.1%) are not rounded away
          const value = Math.round(ratio * 1000) / 10;
          return {
            ageRange: bracket,
            value,
          };
        });
        const hasData = transformed.some((item) => item.value > 0);
        setChartData(hasData ? transformed : []);
      } catch (e: any) {
        return;
      }
    };
    getAgeRatio().catch((err) => console.error(err));
  }, [range, startDate, endDate, gender]);

  const handleGenderSelect = (g: string) => {
    setSelectedGender(g);
    setGender(g === 'ALL' ? undefined : g);
  };

  const barColor = GENDER_COLORS[selectedGender] || colors.dark_green;

  return (
    <Card h='auto' py='24px'>
      <Box width='100%'>
        <CardHeader
          title='Age Range'
          handleChange={handleChange}
          value={range}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        <Flex mt='12px' mb='8px' justifyContent='flex-start'>
          <ButtonGroup size='sm' isAttached variant='outline'>
            {(['ALL', 'MALE', 'FEMALE', 'OTHER'] as const).map((g) => (
              <Button
                key={g}
                onClick={() => handleGenderSelect(g)}
                bg={selectedGender === g ? GENDER_COLORS[g] : 'transparent'}
                color={selectedGender === g ? colors.white : colors.gray}
                borderColor={colors.shade}
                _hover={{
                  bg: selectedGender === g ? GENDER_COLORS[g] : colors.tint
                }}
                fontSize='12px'
              >
                {g}
              </Button>
            ))}
          </ButtonGroup>
        </Flex>
        {chartData.length > 0 ? (
          <ResponsiveContainer width='100%' height={280}>
            <BarChart
              layout='vertical'
              data={chartData}
              margin={{ top: 10, right: 30, left: 10, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#E2E8F0'
                horizontal={false}
              />
              <XAxis
                type='number'
                domain={[0, 100]}
                unit='%'
                tick={{ fontSize: 12, fill: colors.gray }}
                strokeWidth={0}
              />
              <YAxis
                type='category'
                dataKey='ageRange'
                width={130}
                tick={{ fontSize: 12, fill: colors.gray }}
                strokeWidth={0}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={(val: any) => [`${val}%`, 'Percentage']}
                contentStyle={{
                  backgroundColor: colors.white,
                  borderRadius: 8,
                  fontSize: '13px',
                  border: `1px solid ${colors.shade}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              />
              <Bar
                dataKey='value'
                fill={barColor}
                barSize={12}
                radius={[0, 6, 6, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Flex
            justifyContent='center'
            marginX='auto'
            marginY='40px'
            width='100%'
            color={colors.gray}
          >
            {range === 'CUSTOM' && (!startDate || !endDate)
              ? 'Select start and end dates'
              : 'No data available'}
          </Flex>
        )}
      </Box>
    </Card>
  );
};

export default AgeRatioChart;
